import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import dashboardStore from "../../../store/dashboardStore";
import styles from "../../../styles/AETracking.module.css";
import { MixedChart } from "./charts/MixedChart";
import loadingSvg from "../../../../Assets/images/tube-spinner.svg";
import { useTranslation } from "react-i18next";
import { SubPageSwitch } from "../SubPageSwitch";
import { parseDecimalInput } from "../../../utils/parseDecimalInput";
import { useActiveEditor } from "../../../../hooks/useActiveEditor";
import { Selector } from "../../../components/Selector";

//////// mixedChart的input [] 有幾個
const generateActualInput = (startYear, endYear) => {
  return Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i,
  );
};

//////// 年份下拉式選單
const yearOptions = Array.from({ length: 11 }, (_, i) => {
  const year = 2020 + i;
  return {
    label: String(year),
    value: year,
  };
});

const handleChange = (key, val, onChangeFn, setErr) => {
  const result = onChangeFn(key, val);
  if (!result.ok) {
    setErr((prev) => ({
      ...prev,
      [key]: result.error,
    }));
    setTimeout(() => {
      setErr((prev) => ({
        ...prev,
        [key]: "",
      }));
    }, 1500);
    return;
  }
  setErr((prev) => ({
    ...prev,
    [key]: "",
  }));
};

export const AETracking = observer(() => {
  const { t } = useTranslation("ActivityMain");
  const store = dashboardStore;

  ////////輸入下拉式不符合規範的提示判斷
  const [selectorErr, setSelectorErr] = useState({});

  const inputArr = generateActualInput(
    store.mixedChartData.baseYear,
    store.mixedChartData.targetYear,
  );

  ////////註冊這個頁面需要用到的save fn
  useActiveEditor({
    store,
    isEdit: () => store.dashboardIsEdit,
    openWarning: () => {
      store._handleSetShowDashboardWarningModal(true);
    },
    save: async () => {
      await store.updateDecarbonData();
      await store.getDecarbonData();
    },
    clearEdit: () => {
      store._handleSetDashboardIsEdit(false);
    },
  });

  ////////使用者輸入資料以及長條圖資料
  useEffect(() => {
    store.getDecarbonData();
  }, [store.selectedCom]);

  return (
    <div className={styles.AEContainer}>
      {store.AETrackingIsLoading && (
        <div className={styles.loading}>
          <img
            className={styles.loadingImg}
            src={loadingSvg}
            alt="loadingSvg"
          />
        </div>
      )}

      <div className={styles.main}>
        <SubPageSwitch />

        {/* Input Grid */}
        <div className={styles.inputGridContainer}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel} htmlFor="base-year">
              {t("Base Year")}
            </label>
            <Selector
              width={"250px"}
              value={store.mixedChartData.baseYear}
              options={yearOptions}
              handleChange={(key, val) => {
                handleChange(
                  key,
                  val,
                  store._handleSetMixedChartData,
                  setSelectorErr,
                );
              }}
              keyName={"baseYear"}
              error={Boolean(selectorErr.baseYear)}
              helperText={selectorErr.baseYear}
            />
            {/* <input
              className={styles.inputField}
              id="base-year"
              type="text"
              value={2020}
              disabled={true}
            /> */}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel} htmlFor="target-year">
              {t("Target Year")}
            </label>
            {/* <input
              className={styles.inputField}
              id="target-year"
              type="text"
              value={2030}
              disabled={true}
            /> */}
            <Selector
              width={"250px"}
              value={store.mixedChartData.targetYear}
              options={yearOptions}
              handleChange={(key, val) => {
                handleChange(
                  key,
                  val,
                  store._handleSetMixedChartData,
                  setSelectorErr,
                );
              }}
              keyName={"targetYear"}
              error={Boolean(selectorErr.targetYear)}
              helperText={selectorErr.targetYear}
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel} htmlFor="base-emission">
              {t("Base Emission (tCO₂e)")}
            </label>
            <input
              className={styles.inputField}
              id="base-emission"
              type="text"
              value={store.mixedChartData.baseEmission ?? ""}
              onChange={(e) => {
                const result = parseDecimalInput(e.target.value);
                store._handleSetDashboardIsEdit(true);
                store._handleSetMixedChartData("baseEmission", result);
              }}
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel} htmlFor="reduction-rate">
              {t("Annual Reduction Rate (%/yr)")}
            </label>
            <input
              className={styles.inputField}
              id="reduction-rate"
              type="text"
              value={store.mixedChartData.annualReductionRate ?? null}
              onChange={(e) => {
                let inputString = e.target.value;
                const decimalPattern = /^\d*\.?\d*$/;
                if (!decimalPattern.test(inputString)) {
                  return;
                }
                let storeValue;

                if (inputString === "" || inputString === ".") {
                  // 允許空字串或單獨的點，並作為字串儲存，以便用戶繼續輸入
                  storeValue = inputString;
                } else {
                  // 嘗試解析為數字
                  const num = parseFloat(inputString);

                  if (Number.isNaN(num)) {
                    // 不應該發生，因為我們已經用正則過濾過，但作為安全措施
                    return;
                  }

                  // 🌟 修正：如果使用者輸入 '12.' 或 '1.0' 等，我們應保留其字串形式。
                  // 只有在數值為 0 或小於 0 時，才強制設為 0 (數字)
                  if (num <= 0) {
                    storeValue = 0;
                  } else {
                    if (num > 100) {
                      storeValue = 100;
                    } else {
                      storeValue = inputString;
                    }
                  }
                }
                store._handleSetDashboardIsEdit(true);
                store._handleSetMixedChartData(
                  "annualReductionRate",
                  storeValue,
                );
              }}
            />
          </div>
        </div>

        {/* Emissions Grid */}
        <div className={styles.emissionsGrid}>
          <div className={styles.emissionsHeader}>
            {t("Actual Emissions by Year (tCO₂e)")}
            <div className={styles.scopeChecks}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={store.actualData.scope1}
                  onChange={(e) =>
                    store._handleSetActualData("scope1", e.target.checked)
                  }
                />
                {t("Scope 1")}
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={store.actualData.scope2}
                  onChange={(e) =>
                    store._handleSetActualData("scope2", e.target.checked)
                  }
                />
                {t("Scope 2")}
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={store.actualData.scope3}
                  onChange={(e) =>
                    store._handleSetActualData("scope3", e.target.checked)
                  }
                />
                {t("Scope 3")}
              </label>
            </div>
          </div>

          <div className={styles.yearInputWrap}>
            {inputArr?.map((year, i) => {
              const inputValue = store.actualDataMap[year];
              return (
                <div key={year} className={styles.emissionsCell}>
                  <label>{year}</label>
                  <input
                    type="text"
                    disabled={store.actualData?.lock?.includes(year)}
                    value={inputValue ?? ""}
                    onChange={(e) => {
                      const result = parseDecimalInput(e.target.value);
                      store._handleUpdateActualValueByYear(year, result);
                      store._handleSetDashboardIsEdit(true);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Chart Placeholder */}
        <div className={styles.chartPlaceholder}>
          <MixedChart />
        </div>
      </div>
    </div>
  );
});
